import React, { useState, useEffect, useMemo } from "react";
import { User, Mail, Lock, Save, ShieldCheck, KeyRound, CheckCircle2, XCircle, AlertCircle, Eye, EyeOff, MailCheck, Stethoscope, BookOpen, Calendar, Clock, Send, Phone } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const SYMBOLS_REGEX = /[!@#$%^&*()_+\-=[\]{};:,.<>?/|~]/;

function pwChecks(pw) {
  return {
    length: pw.length >= 8,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: SYMBOLS_REGEX.test(pw),
  };
}

function Check({ ok, label }) {
  return (
    <div className={`flex items-center gap-1 text-[11px] ${ok ? "text-emerald-700" : "text-gray-400"}`}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
    </div>
  );
}

export default function Profile() {
  const { t } = useT();
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ full_name: "", email: "", phone: "" });
  const [profileMsg, setProfileMsg] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", new_password_confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [savingPw, setSavingPw] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const [resending, setResending] = useState(false);

  const [tg, setTg] = useState({ linked: false, bot: "", lang: "ar", loading: true });
  const [tgLink, setTgLink] = useState(null);
  const [tgVerifying, setTgVerifying] = useState(false);
  const [tgMsg, setTgMsg] = useState(null);

  useEffect(() => {
    api.get("/telegram/status")
      .then((res) => setTg({ ...res.data, loading: false }))
      .catch(() => setTg((s) => ({ ...s, loading: false })));
  }, []);

  const startTgLink = async () => {
    setTgMsg(null);
    try {
      const res = await api.post("/telegram/link");
      setTgLink(res.data.link);
      window.open(res.data.link, "_blank");
    } catch (err) {
      const d = err.response?.data?.detail;
      setTgMsg({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    }
  };

  const verifyTg = async () => {
    setTgVerifying(true);
    setTgMsg(null);
    try {
      await api.post("/telegram/verify");
      setTg((s) => ({ ...s, linked: true }));
      setTgLink(null);
      setTgMsg({ kind: "success", text: t("tg_linked_success") });
    } catch (err) {
      const d = err.response?.data?.detail;
      setTgMsg({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    } finally { setTgVerifying(false); }
  };

  const unlinkTg = async () => {
    try {
      await api.post("/telegram/unlink");
      setTg((s) => ({ ...s, linked: false }));
      setTgLink(null);
      setTgMsg(null);
    } catch { /* noop */ }
  };

  const [myCert, setMyCert] = useState(null);
  const [certCandidates, setCertCandidates] = useState([]);
  const [claiming, setClaiming] = useState(null);
  const [certLoading, setCertLoading] = useState(true);
  const [showAddCert, setShowAddCert] = useState(false);
  const [certForm, setCertForm] = useState({
    health_expiry_hijri: "",
    training_expiry_hijri: "",
    iqama_number: "",
    employee_id: "",
    phone: "",
    notes: "",
    municipality_card: false,
    printed_photo: false,
  });
  const [savingCert, setSavingCert] = useState(false);
  const [certErr, setCertErr] = useState(null);

  const loadMyCert = async () => {
    try {
      const res = await api.get("/me/health-cert");
      setMyCert(res.data.cert);
      setCertCandidates(res.data.candidates || []);
    } catch { setMyCert(null); setCertCandidates([]); }
    finally { setCertLoading(false); }
  };

  useEffect(() => {
    if (!user || user.role === "super_admin") { setCertLoading(false); return; }
    loadMyCert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const claimCert = async (employee_id) => {
    setClaiming(employee_id);
    try {
      const res = await api.post("/me/health-cert/claim", { employee_id });
      setMyCert(res.data.cert);
      setCertCandidates([]);
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally {
      setClaiming(null);
    }
  };

  const openAddCert = () => {
    setCertErr(null);
    setCertForm((f) => ({
      ...f,
      health_expiry_hijri: myCert?.health_expiry_hijri || "",
      training_expiry_hijri: myCert?.training_expiry_hijri || "",
      iqama_number: myCert?.iqama_number || "",
      employee_id: myCert?.employee_id || "",
      phone: myCert?.phone || user?.phone || "",
      notes: myCert?.notes || "",
      municipality_card: !!myCert?.municipality_card,
      printed_photo: !!myCert?.printed_photo,
    }));
    setShowAddCert(true);
  };

  const saveMyCert = async () => {
    setCertErr(null);
    if (!certForm.health_expiry_hijri || !/^\d{4}-\d{2}-\d{2}$/.test(certForm.health_expiry_hijri)) {
      setCertErr(t("cert_expiry_required"));
      return;
    }
    setSavingCert(true);
    try {
      const res = await api.post("/me/health-cert", certForm);
      setMyCert(res.data.cert);
      setCertCandidates([]);
      setShowAddCert(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      setCertErr(typeof d === "string" ? d : t("save_failed"));
    } finally {
      setSavingCert(false);
    }
  };

  useEffect(() => {
    if (user) setProfile({ full_name: user.full_name || "", email: user.email || "", phone: user.phone || "" });
  }, [user]);

  const checks = useMemo(() => pwChecks(pwForm.new_password), [pwForm.new_password]);
  const pwMatches = pwForm.new_password && pwForm.new_password === pwForm.new_password_confirm;
  const pwOk = Object.values(checks).every(Boolean);

  const saveProfile = async () => {
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", profile);
      await refresh();
      setProfileMsg({ kind: "success", text: t("profile_saved") });
    } catch (err) {
      const d = err.response?.data?.detail;
      setProfileMsg({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    } finally { setSavingProfile(false); }
  };

  const changePw = async () => {
    setPwMsg(null);
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", pwForm);
      setPwMsg({ kind: "success", text: t("password_changed") });
      setPwForm({ current_password: "", new_password: "", new_password_confirm: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setPwMsg({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    } finally { setSavingPw(false); }
  };

  const resendVerify = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      setProfileMsg({ kind: "success", text: t("verification_resent") });
    } catch (err) {
      const d = err.response?.data?.detail;
      setProfileMsg({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    } finally { setResending(false); }
  };

  if (!user) return null;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <div>
        <h2 className="text-xl font-bold text-teal-900 flex items-center gap-2">
          <User size={20} /> {t("nav_profile")}
        </h2>
        <p className="text-sm text-gray-500">{t("profile_subtitle")}</p>
      </div>

      {/* Telegram notifications card */}
      <div className="bg-white rounded-2xl card-shadow p-5" data-testid="telegram-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-xl p-3 text-white" style={{ background: "linear-gradient(135deg,#229ED9,#0f76a8)" }}>
            <Send size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-teal-900">{t("tg_title")}</h3>
            <p className="text-xs text-gray-500">{t("tg_subtitle")}</p>
          </div>
          {tg.linked && (
            <span className="text-[11px] font-black bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full whitespace-nowrap" data-testid="tg-linked-badge">
              ✓ {t("tg_linked")}
            </span>
          )}
        </div>

        {tgMsg && (
          <div className={`text-xs font-bold rounded-xl px-3 py-2 mb-3 ${tgMsg.kind === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="tg-msg">
            {tgMsg.text}
          </div>
        )}

        {tg.loading ? null : tg.linked ? (
          <div className="space-y-3">
            <div>
              <div className="text-xs font-bold text-slate-600 mb-1.5">{t("tg_lang_label")}</div>
              <div className="flex gap-2">
                {["ar", "en"].map((lng) => (
                  <button
                    key={lng}
                    onClick={async () => {
                      try {
                        await api.post("/telegram/lang", { lang: lng });
                        setTg((s) => ({ ...s, lang: lng }));
                      } catch { /* noop */ }
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-black transition ${
                      tg.lang === lng ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    data-testid={`tg-lang-${lng}`}
                  >
                    {lng === "ar" ? "العربية" : "English"}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={unlinkTg} className="btn btn-secondary text-rose-600" data-testid="tg-unlink-btn">
              {t("tg_unlink")}
            </button>
          </div>
        ) : tgLink ? (
          <div className="space-y-3">
            <ol className="text-xs text-slate-600 space-y-1.5 list-decimal ps-5">
              <li>{t("tg_step1")}</li>
              <li>{t("tg_step2")}</li>
              <li>{t("tg_step3")}</li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <a href={tgLink} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="tg-open-link">
                <Send size={13} /> {t("tg_open_bot")}
              </a>
              <button onClick={verifyTg} disabled={tgVerifying} className="btn btn-primary" data-testid="tg-verify-btn">
                {tgVerifying ? t("processing") : t("tg_verify")}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={startTgLink} className="btn btn-primary" data-testid="tg-link-btn">
            <Send size={13} /> {t("tg_link_btn")}
          </button>
        )}
      </div>

      {/* Identity card */}
      <div className="bg-white rounded-2xl card-shadow p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="brand-gradient rounded-xl p-3 text-white">
            <ShieldCheck size={20} />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-teal-900">{user.full_name || user.username}</h3>
            <p className="text-xs text-gray-500" style={{ direction: "ltr" }}>@{user.username}</p>
            <p className="text-[11px] text-gray-400">
              {user.role === "super_admin" ? t("role_super_admin")
                : user.role === "area_manager" ? t("role_area_manager")
                : t("role_supervisor")}
            </p>
          </div>
        </div>

        {!user.email_verified && user.email && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-3" data-testid="email-unverified-banner">
            <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-900 font-semibold">{t("email_unverified_title")}</p>
              <p className="text-xs text-amber-800 mt-0.5">{t("email_unverified_msg").replace("{email}", user.email)}</p>
            </div>
            <button onClick={resendVerify} disabled={resending} className="btn btn-secondary" data-testid="resend-verification">
              <MailCheck size={13} /> {resending ? t("processing") : t("resend_verification")}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="inp-label flex items-center gap-2"><User size={13} /> {t("full_name")}</label>
            <input
              type="text"
              value={profile.full_name}
              onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
              className="inp"
              data-testid="profile-fullname-input"
            />
          </div>
          <div>
            <label className="inp-label flex items-center gap-2"><Mail size={13} /> {t("email")}</label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              className="inp"
              style={{ direction: "ltr" }}
              data-testid="profile-email-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">{t("email_change_hint")}</p>
          </div>
          <div>
            <label className="inp-label flex items-center gap-2"><Phone size={13} /> {t("phone")}</label>
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              className="inp"
              style={{ direction: "ltr" }}
              placeholder="05xxxxxxxx"
              data-testid="profile-phone-input"
            />
          </div>
        </div>

        {profileMsg && (
          <div className={`text-sm rounded-lg p-2 mb-3 ${profileMsg.kind === "error" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="profile-message">
            {profileMsg.text}
          </div>
        )}

        <button onClick={saveProfile} disabled={savingProfile} className="btn btn-primary" data-testid="save-profile-button">
          <Save size={14} /> {savingProfile ? t("saving") : t("save_profile")}
        </button>
      </div>

      {/* My Health Certificate */}
      {user && user.role !== "super_admin" && (
        <div className="bg-white rounded-2xl card-shadow p-5" data-testid="my-health-cert-card">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-bold text-teal-900 flex items-center gap-2">
              <Stethoscope size={18} /> {t("my_health_cert")}
            </h3>
            {!certLoading && (
              <button
                onClick={openAddCert}
                className="btn btn-primary text-xs whitespace-nowrap"
                data-testid="add-my-cert-button"
              >
                {myCert ? t("edit_my_cert_button") : t("add_my_cert_button")}
              </button>
            )}
          </div>
          {certLoading ? (
            <p className="text-sm text-gray-400 py-4">…</p>
          ) : myCert ? (
            <MyCertBody cert={myCert} t={t} />
          ) : (
            <>
              <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 mt-2" data-testid="my-health-cert-empty">
                <AlertCircle size={14} className="inline me-1" />
                {t("my_health_cert_none")}
              </div>
              {certCandidates.length > 0 && (
                <div className="mt-3 border border-teal-100 rounded-xl overflow-hidden" data-testid="cert-candidates">
                  <div className="bg-teal-50 px-3 py-2 text-xs text-teal-900 font-bold border-b border-teal-100">
                    {t("cert_candidates_title")}
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {certCandidates.map((c) => (
                      <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800 truncate">{c.name || "—"}</div>
                          <div className="text-[11px] text-gray-500 flex flex-wrap gap-x-2">
                            {c.position && <span>{c.position}</span>}
                            {c.iqama_number && <span>· {t("iqama_number")}: {c.iqama_number}</span>}
                            {c.employee_id && <span>· {t("employee_id")}: {c.employee_id}</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => claimCert(c.id)}
                          disabled={claiming === c.id}
                          className="btn btn-primary text-xs whitespace-nowrap"
                          data-testid={`claim-cert-${c.id}`}
                        >
                          {claiming === c.id ? t("saving") : t("cert_claim_button")}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-gray-500 px-3 py-2 bg-gray-50 border-t border-gray-100">
                    {t("cert_candidates_help")}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Change password */}
      <div className="bg-white rounded-2xl card-shadow p-5">
        <h3 className="font-bold text-teal-900 flex items-center gap-2 mb-1">
          <KeyRound size={18} /> {t("change_password")}
        </h3>
        <p className="text-xs text-gray-500 mb-4">{t("change_password_subtitle")}</p>

        <div className="space-y-3">
          <div>
            <label className="inp-label flex items-center gap-2"><Lock size={13} /> {t("current_password")}</label>
            <input
              type={showPw ? "text" : "password"}
              value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              className="inp"
              style={{ direction: "ltr" }}
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="inp-label flex items-center gap-2"><Lock size={13} /> {t("new_password")}</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="inp pe-10"
                style={{ direction: "ltr" }}
                data-testid="new-password-input"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute top-1/2 -translate-y-1/2 end-2 text-gray-400 p-1" tabIndex={-1}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1 mt-1.5">
              <Check ok={checks.length} label={t("pw_check_length")} />
              <Check ok={checks.letter} label={t("pw_check_letter")} />
              <Check ok={checks.digit} label={t("pw_check_digit")} />
              <Check ok={checks.symbol} label={t("pw_check_symbol")} />
            </div>
          </div>
          <div>
            <label className="inp-label flex items-center gap-2"><Lock size={13} /> {t("password_confirm")}</label>
            <input
              type={showPw ? "text" : "password"}
              value={pwForm.new_password_confirm}
              onChange={(e) => setPwForm({ ...pwForm, new_password_confirm: e.target.value })}
              className="inp"
              style={{ direction: "ltr" }}
              data-testid="new-password-confirm-input"
            />
            {pwForm.new_password_confirm && (
              <p className={`text-[11px] mt-1 ${pwMatches ? "text-emerald-700" : "text-rose-600"}`}>
                {pwMatches ? t("pw_match_ok") : t("pw_match_fail")}
              </p>
            )}
          </div>

          {pwMsg && (
            <div className={`text-sm rounded-lg p-2 ${pwMsg.kind === "error" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="password-message">
              {pwMsg.text}
            </div>
          )}

          <button
            onClick={changePw}
            disabled={savingPw || !pwForm.current_password || !pwOk || !pwMatches}
            className="btn btn-primary"
            data-testid="change-password-button"
          >
            <KeyRound size={14} /> {savingPw ? t("saving") : t("change_password")}
          </button>
        </div>
      </div>

      {/* Add / Edit My Cert modal */}
      {showAddCert && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-my-cert-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-teal-700 text-white px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold flex items-center gap-2"><Stethoscope size={16} /> {t("my_cert_modal_title")}</h3>
              <button onClick={() => setShowAddCert(false)} className="text-white/80 hover:text-white" data-testid="close-add-my-cert">✕</button>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="text-xs bg-teal-50 border border-teal-100 rounded-lg p-2 text-teal-900">
                {t("my_cert_modal_help")} · <b>{user?.full_name}</b>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="inp-label">{t("health_expiry_hijri")} *</label>
                  <input
                    type="text"
                    placeholder="1447-06-15"
                    className="inp"
                    value={certForm.health_expiry_hijri}
                    onChange={(e) => setCertForm({ ...certForm, health_expiry_hijri: e.target.value })}
                    style={{ direction: "ltr" }}
                    data-testid="my-cert-health-expiry"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">{t("hijri_format_hint")}</p>
                </div>
                <div>
                  <label className="inp-label">{t("training_expiry_hijri")}</label>
                  <input
                    type="text"
                    placeholder="1447-06-15"
                    className="inp"
                    value={certForm.training_expiry_hijri}
                    onChange={(e) => setCertForm({ ...certForm, training_expiry_hijri: e.target.value })}
                    style={{ direction: "ltr" }}
                    data-testid="my-cert-training-expiry"
                  />
                </div>
                <div>
                  <label className="inp-label">{t("iqama_number")}</label>
                  <input
                    type="text"
                    className="inp"
                    value={certForm.iqama_number}
                    onChange={(e) => setCertForm({ ...certForm, iqama_number: e.target.value })}
                    style={{ direction: "ltr" }}
                    data-testid="my-cert-iqama"
                  />
                </div>
                <div>
                  <label className="inp-label">{t("employee_id")}</label>
                  <input
                    type="text"
                    className="inp"
                    value={certForm.employee_id}
                    onChange={(e) => setCertForm({ ...certForm, employee_id: e.target.value })}
                    style={{ direction: "ltr" }}
                    data-testid="my-cert-employee-id"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="inp-label">{t("phone")}</label>
                  <input
                    type="text"
                    className="inp"
                    value={certForm.phone}
                    onChange={(e) => setCertForm({ ...certForm, phone: e.target.value })}
                    style={{ direction: "ltr" }}
                    data-testid="my-cert-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="inp-label">{t("notes")}</label>
                  <textarea
                    className="inp"
                    rows={2}
                    value={certForm.notes}
                    onChange={(e) => setCertForm({ ...certForm, notes: e.target.value })}
                    data-testid="my-cert-notes"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={certForm.municipality_card} onChange={(e) => setCertForm({ ...certForm, municipality_card: e.target.checked })} data-testid="my-cert-mun-card" />
                  <span className="text-sm">{t("municipality_card")}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={certForm.printed_photo} onChange={(e) => setCertForm({ ...certForm, printed_photo: e.target.checked })} data-testid="my-cert-printed-photo" />
                  <span className="text-sm">{t("printed_photo")}</span>
                </label>
              </div>
              {certErr && (
                <div className="text-sm bg-rose-50 border border-rose-200 rounded-lg p-2 text-rose-700" data-testid="my-cert-error">
                  {certErr}
                </div>
              )}
            </div>
            <div className="px-4 py-3 bg-gray-50 flex items-center justify-end gap-2 border-t">
              <button onClick={() => setShowAddCert(false)} className="btn btn-secondary" data-testid="cancel-my-cert">{t("cancel")}</button>
              <button onClick={saveMyCert} disabled={savingCert} className="btn btn-primary" data-testid="save-my-cert">
                <Save size={14} /> {savingCert ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MyCertBody({ cert, t }) {
  const formatHijri = (h) => (h ? `${String(h.d).padStart(2, "0")}/${String(h.m).padStart(2, "0")}/${h.y}` : "—");
  const daysUntil = (iso) => {
    if (!iso) return null;
    const now = new Date();
    const target = new Date(iso);
    return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  };
  const badge = (iso) => {
    const d = daysUntil(iso);
    if (d === null) return { cls: "bg-slate-100 text-slate-600", text: t("missing_cert"), Icon: AlertCircle };
    if (d < 0) return { cls: "bg-rose-100 text-rose-700 border border-rose-300", text: t("expired"), Icon: XCircle };
    if (d <= 30) return { cls: "bg-amber-100 text-amber-800 border border-amber-300", text: `${t("expiring_soon")} · ${d} ${t("days")}`, Icon: Clock };
    return { cls: "bg-emerald-100 text-emerald-800 border border-emerald-300", text: `${t("valid")} · ${d} ${t("days")}`, Icon: CheckCircle2 };
  };
  const h = badge(cert.health_expiry_g);
  const tr = badge(cert.training_expiry_g);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-3">
        <div className="flex items-center gap-2 text-xs font-bold text-teal-900 mb-1">
          <ShieldCheck size={14} /> {t("health_cert")}
        </div>
        <div className="text-lg font-bold text-slate-800">{formatHijri(cert.health_expiry_hijri)}</div>
        <span className={`pill mt-2 inline-flex ${h.cls}`}><h.Icon size={12} /> {h.text}</span>
      </div>
      <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3">
        <div className="flex items-center gap-2 text-xs font-bold text-indigo-900 mb-1">
          <BookOpen size={14} /> {t("training_cert")}
        </div>
        <div className="text-lg font-bold text-slate-800">{formatHijri(cert.training_expiry_hijri)}</div>
        <span className={`pill mt-2 inline-flex ${tr.cls}`}><tr.Icon size={12} /> {tr.text}</span>
      </div>
      {(cert.employee_id || cert.iqama_number || cert.phone) && (
        <div className="md:col-span-2 bg-slate-50 rounded-xl px-3 py-2 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
          {cert.employee_id && <span><b>{t("employee_id")}:</b> {cert.employee_id}</span>}
          {cert.iqama_number && <span><b>{t("iqama_number")}:</b> {cert.iqama_number}</span>}
          {cert.phone && <span><b>{t("phone")}:</b> {cert.phone}</span>}
        </div>
      )}
    </div>
  );
}
